import React, { useState, useEffect, useMemo } from 'react';
import { attributionAPI } from '../../services/api';
import PortfolioSelector from './components/PortfolioSelector';
import SummaryStats from './components/SummaryStats';
import CumulativeBrinsonChart from './components/CumulativeBrinsonChart';
import TopBottomContributors from './components/TopBottomContributors';
import BrinsonAttributionTable from './components/BrinsonAttributionTable';
import { exportAttributionWorkbook } from './exportToExcel';
import './styles.css';
import ManagedDisclaimer from '../../components/ManagedDisclaimer';

const DEFAULT_PORTFOLIO_CODE = 'DRU007SU';
const DEFAULT_REPORT_TYPE = 'SI';

// Portfolios excluded from the dropdown because Refinitiv couldn't generate
// full Brinson data for them (e.g. benchmark doesn't span the portfolio history).
const EXCLUDED_PORTFOLIO_CODES = new Set(['DDP']);

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatPeriodLabel = (period_end, reportType) => {
  const d = new Date(period_end + 'T00:00:00');
  if (reportType === 'MONTHLY') {
    return `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (reportType === '12M') {
    return `12M to ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  }
  const quarter = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${quarter} ${d.getFullYear()}`;
};

// For a RANGE start date: the stored date is a prior month-end (e.g. 2025-12-31),
// but the window it kicks off begins the next day (Jan 2026). Shift forward 1 day
// before formatting so the dropdown reads as the user thinks of it.
const formatStartLabel = (period_end, cadence) => {
  const d = new Date(period_end + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  if (cadence === 'MONTHLY') {
    return `${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  }
  const quarter = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${quarter} ${d.getFullYear()}`;
};

// Subtract N months from an ISO date string (YYYY-MM-DD).
const subtractMonths = (isoDate, months) => {
  const d = new Date(isoDate + 'T00:00:00');
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

// Snap a target ISO date to the closest entry in a sorted list of ISO dates.
const snapToOption = (target, options) => {
  if (!options.length) return target;
  const t = new Date(target + 'T00:00:00').getTime();
  return options.reduce((best, d) => {
    const diff = Math.abs(new Date(d + 'T00:00:00').getTime() - t);
    const bestDiff = Math.abs(new Date(best + 'T00:00:00').getTime() - t);
    return diff < bestDiff ? d : best;
  }, options[0]);
};

const PortfolioAttributionApp = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCode, setSelectedCode] = useState('');
  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPE);
  const [periods, setPeriods] = useState([]);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [reportDetail, setReportDetail] = useState(null);
  const [helpExpanded, setHelpExpanded] = useState(false);

  // RANGE mode state
  const [rangePeriods, setRangePeriods] = useState([]);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [rangePreset, setRangePreset] = useState('1Y');
  const [rangeReportInfo, setRangeReportInfo] = useState(null);

  // Excel export state
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportAttributionWorkbook({
        portfolioCode: selectedCode,
        reportType,
        periodEnd,
        rangeStart,
        rangeEnd,
      });
    } catch (err) {
      console.error('Failed to export workbook', err);
      alert('Export failed — see console for details.');
    } finally {
      setExporting(false);
    }
  };
  const exportDisabled = !selectedCode || (reportType === 'RANGE' && (!rangeStart || !rangeEnd));

  // Load portfolios + default to DRU007SU
  useEffect(() => {
    attributionAPI.getPortfolios()
      .then((data) => {
        const filtered = data.filter(p => !EXCLUDED_PORTFOLIO_CODES.has(p.portfolio_code));
        setPortfolios(filtered);
        const defaultExists = filtered.some(p => p.portfolio_code === DEFAULT_PORTFOLIO_CODE);
        setSelectedCode(defaultExists ? DEFAULT_PORTFOLIO_CODE : (filtered[0]?.portfolio_code || ''));
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load portfolios', err);
        setLoading(false);
      });
  }, []);

  const selectedPortfolio = portfolios.find(p => p.portfolio_code === selectedCode);
  const canUseRange = !!(selectedPortfolio?.has_quarterly || selectedPortfolio?.has_monthly);
  const rangeCadence = selectedPortfolio?.has_monthly ? 'MONTHLY' : 'QUARTERLY';

  // Snap report type to one that's available for this portfolio
  useEffect(() => {
    if (!selectedPortfolio) return;
    if (reportType === 'SI' && selectedPortfolio.has_si === false) {
      // Portfolios with no SI report (e.g. young model series) land on QUARTERLY
      // rather than an empty SI view.
      setReportType(selectedPortfolio.has_quarterly ? 'QUARTERLY'
        : selectedPortfolio.has_12m ? '12M'
        : selectedPortfolio.has_monthly ? 'MONTHLY' : 'SI');
    } else if (reportType === 'MONTHLY' && !selectedPortfolio.has_monthly) {
      setReportType(selectedPortfolio.has_quarterly ? 'QUARTERLY' : 'SI');
    } else if (reportType === '12M' && !selectedPortfolio.has_12m) {
      setReportType(selectedPortfolio.has_quarterly ? 'QUARTERLY' : 'SI');
    } else if (reportType === 'QUARTERLY' && !selectedPortfolio.has_quarterly) {
      setReportType(selectedPortfolio.has_monthly ? 'MONTHLY' : 'SI');
    } else if (reportType === 'RANGE' && !canUseRange) {
      setReportType('SI');
    }
  }, [selectedPortfolio]);

  // Single-period mode: load period list when portfolio or reportType changes
  useEffect(() => {
    if (!selectedCode || reportType === 'SI' || reportType === 'RANGE') {
      setPeriods([]);
      setPeriodEnd(null);
      return;
    }
    attributionAPI.getPeriods(selectedCode, reportType)
      .then(setPeriods)
      .catch(err => console.error('Failed to load periods', err));
    setPeriodEnd(null);
  }, [selectedCode, reportType]);

  // Auto-pick latest when entering QUARTERLY/MONTHLY mode
  useEffect(() => {
    if (reportType !== 'SI' && reportType !== 'RANGE' && periods.length > 0 && periodEnd === null) {
      const latest = periods.find(p => p.is_latest);
      if (latest) setPeriodEnd(latest.period_end);
    }
  }, [reportType, periods]);

  // RANGE mode: fetch the cadence-appropriate period list (used for defaults + presets)
  useEffect(() => {
    if (reportType !== 'RANGE' || !selectedCode) {
      setRangePeriods([]);
      return;
    }
    attributionAPI.getPeriods(selectedCode, rangeCadence)
      .then(setRangePeriods)
      .catch(err => console.error('Failed to load range periods', err));
  }, [reportType, selectedCode, rangeCadence]);

  // RANGE mode: compute start/end from preset whenever preset or periods change
  useEffect(() => {
    if (reportType !== 'RANGE' || rangePeriods.length === 0) return;
    if (rangePreset === 'CUSTOM') return; // user-driven, don't override

    const latestEnd = rangePeriods[0].period_end;
    const oldestStart = rangePeriods[rangePeriods.length - 1].period_start;

    let newStart, newEnd = latestEnd;
    if (rangePreset === 'ALL') {
      newStart = oldestStart;
    } else if (rangePreset === 'YTD') {
      const year = new Date(latestEnd + 'T00:00:00').getFullYear();
      newStart = `${year}-01-01`;
    } else if (rangePreset === '6M') {
      newStart = subtractMonths(latestEnd, 6);
    } else if (rangePreset === '1Y') {
      newStart = subtractMonths(latestEnd, 12);
    } else if (rangePreset === '3Y') {
      newStart = subtractMonths(latestEnd, 36);
    }
    // Snap preset-computed dates to actual available month-ends
    const options = rangePeriods.map(p => p.period_end);
    options.push(rangePeriods[rangePeriods.length - 1].period_start);
    options.sort();
    if (newStart) setRangeStart(snapToOption(newStart, options));
    if (newEnd) setRangeEnd(snapToOption(newEnd, options));
  }, [reportType, rangePreset, rangePeriods]);

  // Single-period mode: load report details
  useEffect(() => {
    if (!selectedCode || reportType === 'RANGE') return;
    const params = { report_type: reportType };
    if (reportType !== 'SI' && periodEnd) params.period_end = periodEnd;
    attributionAPI.getReport(selectedCode, params)
      .then(setReportDetail)
      .catch(err => {
        console.error('Failed to load report detail', err);
        setReportDetail(null);
      });
  }, [selectedCode, reportType, periodEnd]);

  // RANGE mode: load report-info for the window
  useEffect(() => {
    if (reportType !== 'RANGE' || !selectedCode || !rangeStart || !rangeEnd) {
      setRangeReportInfo(null);
      return;
    }
    attributionAPI.getRangeReportInfo(selectedCode, { start_date: rangeStart, end_date: rangeEnd })
      .then(setRangeReportInfo)
      .catch(err => {
        console.error('Failed to load range report info', err);
        setRangeReportInfo(null);
      });
  }, [selectedCode, reportType, rangeStart, rangeEnd]);

  // Available cadence-aligned dates for the RANGE start/end pickers.
  // Each entry is one of the portfolio's period_end values (= month-end for
  // monthly cadence, quarter-end for quarterly), plus the very first
  // period_start so users can include the whole earliest period.
  const rangeDateOptions = useMemo(() => {
    if (rangePeriods.length === 0) return [];
    const dates = new Set();
    rangePeriods.forEach(p => {
      dates.add(p.period_end);
    });
    dates.add(rangePeriods[rangePeriods.length - 1].period_start);
    return Array.from(dates).sort();
  }, [rangePeriods]);

  // Display strings for the report-details strip
  const detailRows = useMemo(() => {
    if (reportType === 'RANGE') {
      if (!rangeReportInfo) return null;
      const cadenceUnit = rangeReportInfo.cadence === 'MONTHLY' ? 'months' : 'quarters';
      return {
        portfolio_name: rangeReportInfo.portfolio_name,
        benchmark_name: rangeReportInfo.benchmark_name,
        period: `${rangeStart} → ${rangeEnd}`,
        as_of: `${rangeReportInfo.n_periods} ${cadenceUnit}`,
        currency: rangeReportInfo.currency,
        attribution_method: rangeReportInfo.attribution_method,
      };
    }
    if (!reportDetail) return null;
    return {
      portfolio_name: reportDetail.portfolio_name,
      benchmark_name: reportDetail.benchmark_name,
      period: `${reportDetail.period_start} → ${reportDetail.period_end}`,
      as_of: reportDetail.as_of_date,
      currency: reportDetail.currency,
      attribution_method: reportDetail.attribution_method,
    };
  }, [reportType, reportDetail, rangeReportInfo, rangeStart, rangeEnd]);

  return (
    <div className="attribution-app">
      <ManagedDisclaimer dkey="notice_attribution_synthetic" className="attribution-disclaimer" />

      <div className="attribution-help">
        <button
          className="attribution-help-toggle"
          onClick={() => setHelpExpanded(v => !v)}
        >
          <span className="attribution-help-chevron">{helpExpanded ? '▼' : '▶'}</span>
          Interpreting Brinson Attribution Results
        </button>
        {helpExpanded && (
          <div className="attribution-help-content">
            <p>
              <strong>Allocation Effect:</strong> measures the impact of being over- or under-weight in asset classes relative to the benchmark. Positive when you overweighted asset classes that outperformed (or underweighted ones that underperformed).
            </p>
            <p>
              <strong>Selection Effect:</strong> measures the impact of the specific manager / fund choices within each asset class. Positive when the managers picked within an asset class outperformed that asset class's benchmark.
            </p>
            <p>
              <strong>Total Effect:</strong> the sum of allocation and selection. The overall Brinson alpha — how much the portfolio's active decisions contributed (positive or negative) vs simply holding the benchmark.
            </p>
          </div>
        )}
      </div>

      <div className="attribution-header">
        <h1>Portfolio Attribution</h1>
        <div className="attribution-controls">
          <PortfolioSelector
            portfolios={portfolios}
            value={selectedCode}
            onChange={setSelectedCode}
            loading={loading}
          />
          <div className="report-type-toggle">
            {selectedPortfolio?.has_si !== false && (
              <label className={reportType === 'SI' ? 'active' : ''}>
                <input
                  type="radio" name="report-type" value="SI"
                  checked={reportType === 'SI'}
                  onChange={() => setReportType('SI')}
                />
                SI
              </label>
            )}
            {selectedPortfolio?.has_12m && (
              <label className={reportType === '12M' ? 'active' : ''}>
                <input
                  type="radio" name="report-type" value="12M"
                  checked={reportType === '12M'}
                  onChange={() => setReportType('12M')}
                />
                12M
              </label>
            )}
            {selectedPortfolio?.has_quarterly && (
              <label className={reportType === 'QUARTERLY' ? 'active' : ''}>
                <input
                  type="radio" name="report-type" value="QUARTERLY"
                  checked={reportType === 'QUARTERLY'}
                  onChange={() => setReportType('QUARTERLY')}
                />
                QUARTERLY
              </label>
            )}
            {(() => {
              const disabled = !selectedPortfolio?.has_monthly;
              const classes = [
                reportType === 'MONTHLY' ? 'active' : '',
                disabled ? 'disabled' : '',
              ].filter(Boolean).join(' ');
              return (
                <label className={classes} title={disabled ? 'No monthly reports for this portfolio' : ''}>
                  <input
                    type="radio" name="report-type" value="MONTHLY"
                    checked={reportType === 'MONTHLY'}
                    onChange={() => setReportType('MONTHLY')}
                    disabled={disabled}
                  />
                  MONTHLY
                </label>
              );
            })()}
            {canUseRange && (
              <label className={reportType === 'RANGE' ? 'active' : ''}>
                <input
                  type="radio" name="report-type" value="RANGE"
                  checked={reportType === 'RANGE'}
                  onChange={() => setReportType('RANGE')}
                />
                RANGE
              </label>
            )}
          </div>
          <select
            className="quarter-selector"
            value={periodEnd || ''}
            onChange={(e) => setPeriodEnd(e.target.value || null)}
            disabled={reportType === 'SI' || reportType === 'RANGE'}
            title={reportType === 'RANGE' ? 'Use the date pickers below in RANGE mode' : ''}
          >
            <option value="">Latest</option>
            {reportType !== 'SI' && reportType !== 'RANGE' && periods.map(p => (
              <option key={p.period_end} value={p.period_end}>
                {formatPeriodLabel(p.period_end, reportType)}{p.is_latest ? ' (latest)' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="export-button"
            onClick={handleExport}
            disabled={exportDisabled || exporting}
            title={exportDisabled ? 'Select a portfolio first' : 'Download attribution workbook (.xlsx)'}
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>
      </div>

      {reportType === 'RANGE' && (
        <div className="range-note">
          Custom date ranges aggregate the Brinson decomposition across the underlying monthly (or quarterly) reports using Cariño geometric linking. Results align tightly with Refinitiv's official quarterly numbers when the window is cadence-aligned, but small residual differences can remain. For an exact match to Refinitiv's quarterly view, switch to the QUARTERLY mode.
        </div>
      )}

      {reportType === 'RANGE' && (
        <div className="range-controls">
          <div className="range-presets">
            {[
              { key: 'YTD', label: 'YTD' },
              { key: '6M', label: 'Last 6M' },
              { key: '1Y', label: 'Last 1Y' },
              { key: '3Y', label: 'Last 3Y' },
              { key: 'ALL', label: 'All available' },
              { key: 'CUSTOM', label: 'Custom' },
            ].map(p => (
              <button
                key={p.key}
                className={`range-preset ${rangePreset === p.key ? 'active' : ''}`}
                onClick={() => setRangePreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="range-dates">
            <label>
              Start
              <select
                value={rangeStart || ''}
                onChange={(e) => { setRangeStart(e.target.value); setRangePreset('CUSTOM'); }}
              >
                {rangeDateOptions
                  .filter(d => !rangeEnd || d < rangeEnd)
                  .map(d => (
                    <option key={d} value={d}>
                      {formatStartLabel(d, rangeCadence)} ({d})
                    </option>
                  ))}
              </select>
            </label>
            <label>
              End
              <select
                value={rangeEnd || ''}
                onChange={(e) => { setRangeEnd(e.target.value); setRangePreset('CUSTOM'); }}
              >
                {rangeDateOptions
                  .filter(d => !rangeStart || d > rangeStart)
                  .map(d => (
                    <option key={d} value={d}>
                      {formatPeriodLabel(d, rangeCadence)} ({d})
                    </option>
                  ))}
              </select>
            </label>
            <span className="range-cadence-note">
              ({rangeCadence === 'MONTHLY' ? 'month-end' : 'quarter-end'} only)
            </span>
          </div>
        </div>
      )}

      <div className="report-details-strip">
        {detailRows ? (
          <>
            <DetailItem label="Portfolio" value={detailRows.portfolio_name} />
            <DetailItem label="Benchmark" value={detailRows.benchmark_name} />
            <DetailItem label="Period" value={detailRows.period} />
            <DetailItem label={reportType === 'RANGE' ? 'Covers' : 'As of'} value={detailRows.as_of} />
            <DetailItem label="Currency" value={detailRows.currency} />
            <DetailItem label="Method" value={detailRows.attribution_method} />
          </>
        ) : (
          <span className="report-details-placeholder">Loading report details…</span>
        )}
      </div>

      <div className="attribution-grid">
        <QuadrantCard title="Summary Statistics">
          <SummaryStats
            portfolioCode={selectedCode}
            reportType={reportType}
            periodEnd={periodEnd}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />
        </QuadrantCard>
        <QuadrantCard title="Cumulative Brinson Chart">
          <CumulativeBrinsonChart
            portfolioCode={selectedCode}
            reportType={reportType}
            periodEnd={periodEnd}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />
        </QuadrantCard>
        <QuadrantCard title="Top / Bottom Contributions">
          <TopBottomContributors
            portfolioCode={selectedCode}
            reportType={reportType}
            periodEnd={periodEnd}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />
        </QuadrantCard>
        <QuadrantCard title="Brinson Attribution Table">
          <BrinsonAttributionTable
            portfolioCode={selectedCode}
            reportType={reportType}
            periodEnd={periodEnd}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
          />
        </QuadrantCard>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="report-detail-item">
    <span className="report-detail-label">{label}</span>
    <span className="report-detail-value">{value || '—'}</span>
  </div>
);

const QuadrantCard = ({ title, placeholder, children }) => (
  <div className="quadrant-card">
    <h2>{title}</h2>
    {children || <div className="quadrant-placeholder">{placeholder}</div>}
  </div>
);

export default PortfolioAttributionApp;
